import {ActiveDiagramItems, HistoryItem as IHistoryItem, SerializedCompareData} from '../../stores/MemoryBarStore';
import {AnalyticsTab, DiagramMode, tKeyAnalyticsTab, tKeyDiagramMode} from '../../stores/store';
import {DateRange} from '../../stores/TimeIntervalStore';
import {DateUtils} from '../../utility/DateUtils';
import {I18n} from '../../i18n/i18n';
import {TableSort, TableSortDirection} from '../../types/Table';
import {CompareMode} from '../../stores/CompareStore';
import {KPI, tKeyKpi} from '../../kpi';
import {BackendFilter, CompareColumnInfo} from '../../stores/GeneralSelectorTableStore';
import React from 'react';
import {px} from 'csx';
import {stylesheet} from 'typestyle';
import {BlueColor} from '../../styles/colors';

export default function MemoryItemOptionRows({
    diagramMode,
    activeFilters,
    mainDateRange,
    activeSearch,
    activeTableSort,
    channel,
    analyticsTab,
    activeDiagramItems,
    getAttributeNameIfPossible,
    allowDateCompareRange,
    compareDateRange,
    compareData,
}: IHistoryItem & {getAttributeNameIfPossible: (kpi: KPI) => string}) {
    return (
        <div>
            {activeDiagramItems !== undefined && (activeDiagramItems.diagrams.length > 0 || activeDiagramItems.channelDiagrams.length > 0) ? (
                <DrawnDiagramOptionRow
                    diagramItems={activeDiagramItems}
                    mainDateRange={mainDateRange}
                    compareDateRange={compareDateRange}
                    allowDateCompareRange={allowDateCompareRange}
                    getAttributeNameIfPossible={getAttributeNameIfPossible}
                />
            ) : null}
            {diagramMode !== DiagramMode.Hourly ? <DiagramOptionRow diagramMode={diagramMode} /> : null}
            {activeFilters.length > 0 ? <FilterOptionRow activeFilters={activeFilters} /> : null}
            {activeSearch !== '' ? <SearchOptionRow activeSearch={activeSearch} /> : null}
            {activeTableSort && activeTableSort.attribute !== null && activeTableSort.sortDirection !== null ? (
                <TableSortOptionRow activeTableSort={activeTableSort} />
            ) : null}
            {channel ? <ChannelOptionRow channel={channel} /> : null}
            {compareData ? <ComparisonRow compareData={compareData} /> : null}
            <AnalyticsTabOptionRow analyticsTab={analyticsTab} />
        </div>
    );
}

function DrawnDiagramOptionRow({
    diagramItems,
    getAttributeNameIfPossible,
    mainDateRange,
    compareDateRange,
    allowDateCompareRange,
}: {
    diagramItems: ActiveDiagramItems;
    getAttributeNameIfPossible: (kpi: KPI) => string;
    mainDateRange: DateRange;
    compareDateRange: DateRange;
    allowDateCompareRange: boolean;
}) {
    const dateRange = DateUtils.formatDateRange(compareDateRange.startDate, compareDateRange.endDate);

    return (
        <div className={optionRowCss.settingRow}>
            <div>
                <i className={'fa fa-line-chart ' + optionRowCss.icon} />
                <div style={{display: 'inline-block'}}>
                    {diagramItems.channelDiagrams.map((channelDiagram) => (
                        <div
                            className={optionRowCss.lineChartMemoryItem}
                            key={channelDiagram.kpi + channelDiagram.channel + channelDiagram.testId + channelDiagram.variantId}>
                            {channelDiagram.channel} - {getAttributeNameIfPossible(channelDiagram.kpi)}{' '}
                            {channelDiagram.testId && channelDiagram.variantId ? ` - ${channelDiagram.testId} - ${channelDiagram.variantId}` : ''}
                        </div>
                    ))}
                    {diagramItems.diagrams.map((diagram) => (
                        <div
                            key={diagram.searchtermOrProductId + diagram.kpi + diagram.channel + diagram.testId + diagram.variantId}
                            className={optionRowCss.lineChartMemoryItem}>
                            {diagram.channel} - {diagram.searchtermOrProductId} - {getAttributeNameIfPossible(diagram.kpi)}{' '}
                            {diagram.testId && diagram.variantId ? ` - ${diagram.testId} - ${diagram.variantId}` : ''}
                        </div>
                    ))}
                    {diagramItems.showAbsoluteValues === false ? (
                        <div className={optionRowCss.lineChartMemoryItem} style={{fontWeight: 'bold'}}>
                            <I18n i18nKey="chart:relativeText" />
                        </div>
                    ) : null}
                    <div className={optionRowCss.lineChartMemoryItem} style={{fontWeight: 'bold'}}>
                        {DateUtils.formatDateRange(mainDateRange.startDate, mainDateRange.endDate)}
                    </div>
                    {allowDateCompareRange ? (
                        <div className={optionRowCss.lineChartMemoryItem} style={{fontWeight: 'bold'}}>
                            <I18n i18nKey="memorybar.compare" values={{dateRange}} />
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

function ComparisonRow({compareData}: {compareData?: SerializedCompareData}) {
    if (!compareData) {
        return null;
    }

    const compareTargets = (() => {
        if (compareData.channelCompare) {
            return compareData.channelCompare.channelNames;
        } else if (compareData.variantChannelCompare) {
            return compareData.variantChannelCompare.variantIds;
        }
        return [];
    })();

    return (
        <div className={optionRowCss.settingRow}>
            <i className={'fa fa-balance-scale ' + optionRowCss.icon} />
            <div className={optionRowCss.lineChartModeValue}>{compareTargets.toString()}</div>
        </div>
    );
}
function AnalyticsTabOptionRow({analyticsTab}: {analyticsTab: AnalyticsTab}) {
    return (
        <div className={optionRowCss.settingRow}>
            <i className={'fa fa-folder-o ' + optionRowCss.icon} />
            <div className={optionRowCss.lineChartModeValue}>
                <I18n i18nKey={tKeyAnalyticsTab(analyticsTab)} />
            </div>
        </div>
    );
}

function ChannelOptionRow({channel}: {channel: string}) {
    return (
        <div className={optionRowCss.settingRow}>
            <i className={'fa fa-code-fork ' + optionRowCss.icon} />
            <div className={optionRowCss.lineChartModeValue}>{channel ? channel : ''}</div>
        </div>
    );
}

function generateCompareModeAttribute(activeTableSort: TableSort) {
    switch (activeTableSort.compareMode) {
        case CompareMode.AbsDiffMode:
            return (
                <div>
                    <I18n i18nKey={tKeyKpi(activeTableSort.attribute)} /> - <I18n i18nKey={'table:difference'} />
                </div>
            );
        case CompareMode.ChannelOrVariantColumn:
            return (
                <div>
                    <I18n i18nKey={tKeyKpi(activeTableSort.attribute)} />
                    {'-' + activeTableSort.targetName}
                </div>
            );
        case CompareMode.RatioMode:
            return (
                <div>
                    <I18n i18nKey={tKeyKpi(activeTableSort.attribute)} /> - <I18n i18nKey={'table:compare'} />
                </div>
            );
        default:
            return <I18n i18nKey={tKeyKpi(activeTableSort.attribute)} />;
    }
}

function TableSortOptionRow({activeTableSort}: {activeTableSort: TableSort}) {
    const attributeRowValue = generateCompareModeAttribute(activeTableSort);

    return (
        <div className={optionRowCss.settingRow}>
            {activeTableSort.sortDirection === TableSortDirection.DESC ? (
                <i className={'fa fa-sort-amount-desc ' + optionRowCss.icon} />
            ) : (
                <i className={'fa fa-sort-amount-asc ' + optionRowCss.icon} />
            )}
            <div className={optionRowCss.lineChartModeValue}>{attributeRowValue}</div>
        </div>
    );
}

function SearchOptionRow({activeSearch}: {activeSearch: string}) {
    return (
        <div className={optionRowCss.settingRow}>
            <i className={'fa fa-search ' + optionRowCss.icon} />
            <div className={optionRowCss.lineChartModeValue}>
                {'"'}
                {activeSearch}
                {'"'}
            </div>
        </div>
    );
}

function FilterOptionRow({activeFilters}: {activeFilters: BackendFilter[]}) {
    const generateCompareModeAttributeAddition = (compareColumnInfo?: CompareColumnInfo): React.ReactNode => {
        if (!compareColumnInfo) {
            return null;
        }
        switch (compareColumnInfo.compareMode) {
            case CompareMode.AbsDiffMode:
                return (
                    <span>
                        {' - '}
                        <I18n i18nKey={'table:difference'} />
                    </span>
                );
            case CompareMode.ChannelOrVariantColumn:
                return <span>{' - ' + compareColumnInfo.targetName}</span>;
            default:
                return null;
        }
    };

    const getUniqueCompareKey = (compareColumnInfo?: CompareColumnInfo): string => {
        if (!compareColumnInfo) {
            return '';
        }

        switch (compareColumnInfo.compareMode) {
            case CompareMode.RatioMode:
            case CompareMode.AbsDiffMode:
                return compareColumnInfo.compareMode;
            case CompareMode.ChannelOrVariantColumn:
                return compareColumnInfo.compareMode + compareColumnInfo.targetName;
            default:
                return '';
        }
    };

    return (
        <div className={optionRowCss.settingRow}>
            <i className={'fa fa-filter ' + optionRowCss.icon} />
            <div className={optionRowCss.lineChartModeValue}>
                <ul className={optionRowCss.filterList}>
                    {activeFilters.map((data) => (
                        <li key={data.attribute + data.comparator + data.value + getUniqueCompareKey(data.compareColumnInfo)}>
                            <I18n i18nKey={tKeyKpi(data.attribute)} />
                            {generateCompareModeAttributeAddition(data.compareColumnInfo)} {data.comparator} {data.value}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

function DiagramOptionRow({diagramMode}: {diagramMode: DiagramMode}) {
    return (
        <div className={optionRowCss.settingRow}>
            <i className={'fa fa-clock-o ' + optionRowCss.icon} />
            <div className={optionRowCss.lineChartModeValue}>
                <I18n i18nKey={tKeyDiagramMode(diagramMode)} />
            </div>
        </div>
    );
}

const optionRowCss = stylesheet({
    settingRow: {
        position: 'relative',
        borderBottom: '1px solid ' + BlueColor,
        $nest: {
            '&:last-child': {
                borderBottom: 0,
            },
        },
    },
    icon: {
        position: 'absolute',
        top: 5,
        left: 0,
    },
    lineChartModeValue: {
        display: 'inline-block',
        paddingLeft: 25,
        fontSize: px(12),
    },
    lineChartMemoryItem: {
        display: 'block',
        paddingLeft: 25,
        fontSize: px(12),
    },
    filterList: {
        paddingLeft: 0,
        marginBottom: 0,
        listStyleType: 'none',
    },
});



// WEBPACK FOOTER //
// ./src/views/MemoryBar/MemoryItemOptionRows.tsx